/**
 * M12a — Cancel an in-flight cortex run by sending KIND_AGENT_CANCEL.
 *
 * The cancel surface is server-side complete after M11c:
 *   - nexus gateway/handler.rs::handle_agent_cancel forwards to cortex
 *   - cortex control.py sets the cancel_event
 *   - event_stream.py::stream_run_events does two-phase soft/hard cancel
 *     and emits RunDone { cancelled: true }, which becomes a KIND_AGENT_DONE
 *     envelope with body.cancelled = true on the wire.
 *
 * The hook just emits the cancel envelope. The downstream useAssistantChat
 * hook's onConversationDone callback observes the cancelled flag and reacts.
 */

import { useCallback } from 'react';
import { useSyncTransport } from '@/lib/sync/sync-system-context';
import { encodeEnvelope } from '@/lib/sync/envelope';
import { KIND_AGENT_CANCEL } from '@/lib/sync/envelope-kinds';

export interface UseCancelRunReturn {
  /**
   * Cancel an in-flight run. Throws `Error('not_connected')` when the
   * SyncTransport is not in 'connected' state.
   */
  cancel: (runId: string) => void;
}

export function useCancelRun(): UseCancelRunReturn {
  const syncTransport = useSyncTransport();

  const cancel = useCallback(
    (runId: string) => {
      const frame = encodeEnvelope({
        kind: KIND_AGENT_CANCEL,
        corrId: runId,
      });
      const ok = syncTransport.send(frame);
      if (!ok) {
        throw new Error('not_connected');
      }
    },
    [syncTransport],
  );

  return { cancel };
}
